import React, { useState } from 'react'
import { Formik, FieldArray } from 'formik'
import get from 'lodash/get'
import * as Yup from 'yup'
import { t } from '../../i18n'

const DosingModeConfigurationForm = ({
  dosingModeConfiguration = {
    no_change_max_time: 10000,
    no_change_amount: 1,
    max_emergency_attempts: 0,
    emergency_feeder_dosing_mode_parameters: [
      {
        id: null, pressure: 0, frequency: 0, duty_cycle: 0,
        mode_type: 'emergency', step_number: 1, duration: 0, _destroy: false
      }
    ]
  },
  onSubmitForm
}) => {

  const validationSchema = Yup.object({
    no_change_max_time: Yup.number()
      .required(t('dosing_mode_configuration.validations.no_change_max_time.required'))
      .min(10000, t('dosing_mode_configuration.validations.no_change_max_time.min', { min: 10000 }))
      .max(60000, t('dosing_mode_configuration.validations.no_change_max_time.max', { max: 60000 })),
    no_change_amount: Yup.number()
      .required(t('dosing_mode_configuration.validations.no_change_amount.required'))
      .min(1, t('dosing_mode_configuration.validations.no_change_amount.min', { min: 1 }))
      .max(100, t('dosing_mode_configuration.validations.no_change_amount.max', { max: 100 })),
    max_emergency_attempts: Yup.number()
      .required(t('dosing_mode_configuration.validations.max_emergency_attempts.required'))
      .min(0, t('dosing_mode_configuration.validations.max_emergency_attempts.min', { min: 0 }))
      .max(5, t('dosing_mode_configuration.validations.max_emergency_attempts.max', { max: 5 })),
    emergency_feeder_dosing_mode_parameters: Yup.array().of(
      Yup.object({
        pressure: Yup.number()
          .required(t('feeder_dosing_modes.validations.pressure.required'))
          .min(0.1, t('feeder_dosing_modes.validations.pressure.min', { min: 0.1 }))
          .max(6.0, t('feeder_dosing_modes.validations.pressure.max', { max: 6.0 })),
        frequency: Yup.number()
          .required(t('feeder_dosing_modes.validations.frequency.required'))
          .min(1.0, t('feeder_dosing_modes.validations.frequency.min', { min: 1.0 }))
          .max(25.0, t('feeder_dosing_modes.validations.frequency.max', { max: 25.0 })),
        duty_cycle: Yup.number()
          .required(t('feeder_dosing_modes.validations.duty_cycle.required'))
          .min(0, t('feeder_dosing_modes.validations.duty_cycle.min', { min: 0 }))
          .max(100, t('feeder_dosing_modes.validations.duty_cycle.max', { max: 100 })),
        duration: Yup.number()
          .required(t('feeder_dosing_modes.validations.duration.required'))
          .min(0, t('feeder_dosing_modes.validations.duration.min', { min: 0 }))
          .max(30000, t('feeder_dosing_modes.validations.duration.max', { max: 30000 })),
      })
    ).max(3, t('dosing_mode_configuration.validations.max_emergency_dosing_modes'))
  });

  return (
    <Formik
      initialValues={dosingModeConfiguration}
      validationSchema={validationSchema}
      onSubmit={(values, { setStatus, setSubmitting }) => {
        const payload = { ...values }
        return onSubmitForm(payload, (success, errorData) => {
          let status = {}
          if (!success) {
            const fields = Object.getOwnPropertyNames(errorData.full_errors)
            for (let key in fields) {
              status[`${fields[key]}_error`] =
                errorData.full_errors[fields[key]].join(', ')
            }
          }
          setStatus(status)
          setSubmitting(false)
        })
      }}
    >
      {({
        values,
        errors,
        touched,
        status,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        setFieldValue,
        setStatus,
      }) => {
        const enhancedHandleChange = (e) => {
          let newStatus = Object.assign({}, status)
          newStatus[`${e.target.name}_error`] = ''
          setStatus(newStatus)
          handleChange(e)
        }

        const renderField = (label, name, type, placeholder, value=null, inverted = true, autoComplete = 'off') => {
          const inputValue = value !== null ? value : values[name]
          const errorsPresent = (get(errors, name) && get(touched, name)) || (status && status[`${name}_error`])
          const errorMessage =
          typeof get(errors, name) === 'string'
            ? get(errors, name)
            : get(errors, name)?.required ||
              (status && status[`${name}_error`]);
          return (
            <div className="form-group">
              <label>{label}</label>
              <input
                id={name}
                name={name}
                type={type}
                onBlur={handleBlur}
                value={inputValue === null ? '' : inputValue}
                placeholder={placeholder}
                autoComplete={autoComplete}
                onChange={enhancedHandleChange}
                className={`form-control ${errorsPresent && 'is-invalid'} ${inverted && 'inverted'}`}
                step="any"
              />
              {errorsPresent && (
                <div className="invalid-feedback">
                   {errorMessage}
                </div>
              )}
            </div>
          )
        }

        return (
          <form onSubmit={handleSubmit} className="form-robis">
            {renderField(
              t('dosing_mode_configurations.no_change_max_time'),
              'no_change_max_time',
              'number',
              t('dosing_mode_configurations.enter_no_change_max_time')
            )}
            {renderField(
              t('dosing_mode_configurations.no_change_amount'),
              'no_change_amount',
              'number',
              t('dosing_mode_configurations.enter_no_change_amount')
            )}
            {renderField(
              t('dosing_mode_configurations.max_emergency_attempts'),
              'max_emergency_attempts',
              'number',
              t('dosing_mode_configurations.enter_max_emergency_attempts')
            )}

            <FieldArray name="emergency_feeder_dosing_mode_parameters">
              {({ remove, push }) => {
                const sortedParameters = values.emergency_feeder_dosing_mode_parameters
                  .filter((move) => !move._destroy)
                  .sort((a, b) => a.step_number - b.step_number);

                const nextStepNumber =
                sortedParameters.length > 0
                    ? Math.max(...sortedParameters.map((step) => step.step_number)) + 1
                    : 1;

                const renumberSteps = (steps) => {
                  const reorderedSteps = steps
                    .filter((step) => !step._destroy)
                    .sort((a, b) => a.step_number - b.step_number)
                    .map((step, index) => ({
                      ...step,
                      step_number: index + 1,
                    }));

                  return reorderedSteps.concat(steps.filter((step) => step._destroy));
                };

                return (
                  <div>
                    <h5 style={{ margin: '40px 0 20px 10px' }}>
                      {t('emergency_feeder_dosing_mode_parameters.title')}
                    </h5>
                    {sortedParameters.map((_, index) => (
                      <div
                        key={index}
                        className="emergency-parameter-fields"
                        style={{
                          backgroundColor: 'white',
                          padding: '20px',
                          borderRadius: '20px',
                          marginBottom: '20px',
                        }}
                      >
                        <h6 style={{ margin: '10px 0 20px 10px' }}>
                          {t('emergency_feeder_dosing_mode_parameters.emergency_feeder_dosing_mode_parameter')} #{index + 1}
                        </h6>

                        {renderField(
                          t('feeder_dosing_modes.pressure'),
                          `emergency_feeder_dosing_mode_parameters[${index}].pressure`,
                          'number',
                          t('feeder_dosing_modes.enter_pressure'),
                          values.emergency_feeder_dosing_mode_parameters[index].pressure,
                          false
                        )}

                        {renderField(
                          t('feeder_dosing_modes.frequency'),
                          `emergency_feeder_dosing_mode_parameters[${index}].frequency`,
                          'number',
                          t('feeder_dosing_modes.enter_frequency'),
                          values.emergency_feeder_dosing_mode_parameters[index].frequency,
                          false
                        )}

                        {renderField(
                          t('feeder_dosing_modes.duty_cycle'),
                          `emergency_feeder_dosing_mode_parameters[${index}].duty_cycle`,
                          'number',
                          t('feeder_dosing_modes.enter_duty_cycle'),
                          values.emergency_feeder_dosing_mode_parameters[index].duty_cycle,
                          false
                        )}

                        {renderField(
                          t('feeder_dosing_modes.duration'),
                          `emergency_feeder_dosing_mode_parameters[${index}].duration`,
                          'number',
                          t('feeder_dosing_modes.enter_duration'),
                          values.emergency_feeder_dosing_mode_parameters[index].duration,
                          false
                        )}

                        <button
                          type="button"
                          className={`btn btn-danger btn-sm remove-param-${index}`}
                          onClick={() => {
                            const updatedParameters = [...values.emergency_feeder_dosing_mode_parameters];
                            updatedParameters[index]._destroy = true;

                            const renumberedSteps = renumberSteps(updatedParameters);

                            setFieldValue('emergency_feeder_dosing_mode_parameters', renumberedSteps);
                          }}
                          disabled={sortedParameters.length <= 1}
                        >
                          {t('emergency_feeder_dosing_mode_parameters.remove')}
                        </button>
                      </div>
                    ))}
                    {sortedParameters.length < 3 && (
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() =>
                          push({
                            pressure: 0,
                            frequency: 0,
                            duty_cycle: 0,
                            duration: 0,
                            step_number: nextStepNumber,
                            mode_type: 'emergency',
                            _destroy: false,
                          })
                        }
                      >
                        <i className="fa fa-plus" style={{ marginRight: '5px' }} />
                        {t('emergency_feeder_dosing_mode_parameters.add')}
                      </button>
                    )}
                  </div>
                );
              }}
            </FieldArray>

            <div className="form-actions d-flex justify-content-end">
              <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                {t('save')}
              </button>
            </div>
          </form>
        )
      }}
    </Formik>
  )
}

export default DosingModeConfigurationForm
